import { GateCheckInStatus } from "../../../utils/constants";
import {
  applyStopRules,
  initialGateStatus,
} from "../../../utils/driverStops";
import { sessionReloadYard, yardDriverStops } from "../../../utils/gateAccess";
import {
  departureNeedsWorkDoneReason,
  isShipmentWorkDone,
} from "../../../utils/gateQueueOrder";

const { DOCKED, CALLED, CANCELLED, ARRIVED, DEPARTED } = GateCheckInStatus;

/** Couples the shipment dispatch tab's date pickers to the driver's gate
 * check-in, in both directions.
 *
 * The inbound and outbound dispatch tabs held their own copies of this logic —
 * three of the five functions byte-identical, the other two differing only in
 * which field holds the arrival and which fields block a placement. The copies
 * had already drifted: outbound's DEPARTED branch required
 * `shipmentLineItems?.items?.length > 0` while its Time Released picker only
 * disabled on `=== 0`, so a form whose line items were absent from the form
 * values (rather than an empty list) offered the manager a picker they could
 * use by hand but refused to stamp from the gate, with nothing on screen to
 * explain the difference. Both sides now come from one predicate per
 * direction, supplied by the caller.
 *
 * Not a hook: it calls nothing from React, and keeping it a plain factory means
 * the state machine can be tested against a stub form.
 *
 * The pending edit is staged in the form (gateCheckInEdit) and persisted by the
 * form's primary Save via resolveStagedGateEdit / commitStagedGateEdit —
 * nothing here writes to the API.
 */
export const createStagedGateEdit = ({
  form,
  gateCheckIn,
  notify,
  /** The yard record, for its stop route (applyStopRules) and for the status
   * a row minted here opens at (initialGateStatus). Read from the session by
   * default; a test hands its own in. */
  reloadYard,
  /** What differs between the two directions:
   *  - arrivalField / placedField: the shipment's own stamp field names
   *  - setArrival(value, formData): the tab's arrival handler, which owns the
   *    cascade a cleared arrival triggers (and is also called with null to roll
   *    an arrival back)
   *  - setPlaced(stamp, formData): the tab's placed-time handler
   *  - isAtDockBlocked(formData): the load has already moved past spotting, so
   *    a dock stamp would rewrite history. Silent: this is the expected state
   *    every time an already-placed row is reopened, and it is not a refusal.
   *  - atDockRefusalReason(formData): optional; the load may not be placed at
   *    all yet (no carrier or trailer; inbound: no inventory, and not SIT —
   *    gatePlacementRefusal for the tab's direction). Returns the reason to
   *    show the manager, or null. Unlike isAtDockBlocked this is a refusal of
   *    something the manager expected to happen, so it must say why — and a
   *    DOCKED pick it refuses is not staged at all, since saveGateCheckIn
   *    would refuse the same write.
   *  - canStampRelease(formData) / setRelease(stamp): the release stamp and the
   *    same predicate the Time Released picker uses for its `disabled` prop
   *  - shipmentOf(formData): optional; the load as the departure rule should
   *    see it (record under form values — the form only holds what its own
   *    tabs put there, and `state` may not be one of them). Defaults to the
   *    form values.
   */
  fields,
}) => {
  const {
    arrivalField,
    placedField,
    setArrival,
    setPlaced,
    isAtDockBlocked,
    atDockRefusalReason,
    canStampRelease,
    setRelease,
    shipmentOf = (formData) => formData,
  } = fields;

  // Stage a pending gate check-in edit in the form; the primary Save persists
  // it together with the shipment. The first staging pins the row and status
  // the manager is looking at (checkInId, baseStatus) — the save conditions
  // its write on that base, so a row the gate crew advanced (or replaced with
  // a new driver's check-in) meanwhile raises a conflict instead of being
  // silently regressed.
  const setGateEdit = (edits) => {
    const current = form.getState().values.gateCheckInEdit || {};
    // No row to pin: the office is checking a truck in from this tab, and the
    // edit holds the opening values of the row the save will mint (`manual` is
    // what routes it there instead of at an existing row). Only while the form
    // carries the arrival that row is dated from — clear the arrival and
    // nothing is minted, so anything typed here would be dropped in silence.
    if (!gateCheckIn) {
      if (!form.getState().values[arrivalField]) return;
      form.change("gateCheckInEdit", { manual: true, ...current, ...edits });
      return;
    }
    form.change("gateCheckInEdit", {
      checkInId: gateCheckIn.id,
      baseStatus: gateCheckIn.status,
      ...current,
      ...edits,
    });
  };

  /** The office is checking a truck in that never reached the kiosk: stamp the
   * arrival the row is dated from — which is also what carries the shipment to
   * ARRIVED on this save — and open the control on it. The row itself is minted
   * by the save (createManualGateCheckIn), so nothing is written here and the
   * manager can still back out by clearing the arrival. */
  const startManualGateCheckIn = () => {
    const formData = form.getState().values;
    if (!formData[arrivalField]) setArrival(new Date().toISOString(), formData);
    // The row opens the way a kiosk row would: waiting where the yard has
    // stops to call the driver through (or has never saved a list), CALLED
    // where it saved none.
    setGateEdit({ status: initialGateStatus(yard()) });
  };

  const effectiveGateStatus = () =>
    form.getState().values.gateCheckInEdit?.status ?? gateCheckIn?.status;
  const effectiveDock = () =>
    form.getState().values.gateCheckInEdit?.dockAssignment ??
    gateCheckIn?.dockAssignment ??
    "";
  const yard = () => reloadYard ?? sessionReloadYard();
  const stops = () => yardDriverStops(yard());

  /** The stop picked for the driver. Picking one for a waiting driver is the
   * call, so the status is staged with it — through handleGateStatus, so the
   * arrival stamp a call owes follows too. Clearing the stop that made the
   * call takes the call back. */
  const handleGateDock = (dockAssignment) => {
    const current = effectiveGateStatus();
    const ruled = applyStopRules({
      stops: stops(),
      current: gateCheckIn,
      status: current,
      dockAssignment,
    });
    if (ruled.status !== current) {
      handleGateStatus(ruled.status, dockAssignment);
      return;
    }
    if (
      !dockAssignment &&
      current === CALLED &&
      gateCheckIn?.status === ARRIVED &&
      form.getState().values.gateCheckInEdit?.status === CALLED
    ) {
      setGateEdit({ status: ARRIVED, dockAssignment });
      return;
    }
    setGateEdit({ dockAssignment });
  };

  // Date-picker to gate-status coupling: only advance out of the waiting
  // statuses; a pending DOCKED/DEPARTED/CANCELLED pick is never overwritten
  // by a date edit.
  const setGateStatusForward = (status) => {
    // A row staged for minting advances with the date pickers exactly as a
    // real one does — the manager who places the load before saving means the
    // truck reached a dock, whether or not its row exists yet.
    if (!gateCheckIn && !form.getState().values.gateCheckInEdit?.manual) return;
    const current = effectiveGateStatus();
    if (current === status || current === CANCELLED || current === DEPARTED)
      return;
    if (status === DOCKED && current !== ARRIVED && current !== CALLED)
      return;
    // A release stamp ends the gate visit only for a truck that is at a dock
    // with its load worked. Typed for a driver still on the way (a no-show
    // being closed out), it stages nothing: the row is the gate's to cancel,
    // and staging DEPARTED here would refuse the whole shipment save for a
    // status the manager never picked.
    if (
      status === DEPARTED &&
      (current !== DOCKED ||
        !isShipmentWorkDone(shipmentOf(form.getState().values)))
    )
      return;
    setGateEdit({ status });
  };

  // Moving the driver to DOCKED means the load is spotted. Stamp the open
  // form rather than writing the shipment behind the manager's back — the next
  // save would overwrite a direct write with this form's stale placed time,
  // and the save path derives PLACED from the placed stamp anyway.
  //
  // Also reached from the poll, when the crew docked the driver from their
  // Gate Queue while this form sat open (GateCheckInControl's onAtDock). That
  // row is already DOCKED and the crew page placed the shipment before it
  // wrote it, so a refusal here can only mean the form is behind the row —
  // say why the placed time did not follow, or the manager is left with a
  // dock assignment and an unexplained empty field.
  const handleGateAtDock = (stamp) => {
    const formData = form.getState().values;
    if (isAtDockBlocked(formData)) return;
    const refusal = atDockRefusalReason?.(formData);
    if (refusal) {
      notify(`${refusal} — placed time not set`, "warning");
      return;
    }
    setPlaced(stamp, formData);
    notify("Placed time set from the gate — save to apply");
  };

  // Gate-status to date-field coupling. ARRIVED/CALLED stamp the arrival
  // time, DOCKED the placed time, DEPARTED the released time — each only
  // when the field is empty and the picker would have allowed it, so a
  // manager's earlier stamp is never overwritten. CANCELLED rolls an un-placed
  // arrival back, which the save path turns into the check-in cancel.
  const handleGateStatus = (status, dockAssignment = effectiveDock()) => {
    const formData = form.getState().values;
    // DOCKED is refused outright — not staged — when the shipment cannot be
    // placed: the same rule saveGateCheckIn applies at the write, asked here
    // where the form already holds the answer. Staging the status anyway
    // would put a DOCKED row over an ARRIVED shipment on the next save, and
    // the crew Gate Queue, which offers its button to a CALLED driver only,
    // would show that truck with nothing to do. A load past spotting is not
    // a refusal: the driver is at the dock of a shipment already placed.
    if (status === DOCKED && !isAtDockBlocked(formData)) {
      const refusal = atDockRefusalReason?.(formData);
      if (refusal) {
        notify(`${refusal} — the driver was not docked`, "warning");
        return;
      }
    }
    // Likewise DEPARTED over a load still being worked: the select greys it,
    // and this is the guard behind a select drawn before the form changed.
    if (
      status === DEPARTED &&
      gateCheckIn?.status !== DEPARTED &&
      !isShipmentWorkDone(shipmentOf(formData))
    ) {
      notify(
        `${departureNeedsWorkDoneReason()} — the driver was not marked departed`,
        "warning",
      );
      return;
    }
    // The stop rides with the status: a call names the first route stop, a
    // dock the last, unless one was picked already. ARRIVED picked back on a
    // waiting row (or a row being minted, which opens waiting) takes back the
    // call staged here, stop and all — the mirror of handleGateDock's clear.
    // Staged with the stop kept, the write would read that stop as the call
    // and save CALLED under a select that says ARRIVED.
    if (status === ARRIVED && (!gateCheckIn || gateCheckIn.status === ARRIVED)) {
      setGateEdit({ status, dockAssignment: gateCheckIn?.dockAssignment ?? "" });
    } else {
      const ruled = applyStopRules({
        stops: stops(),
        current: gateCheckIn,
        status,
        dockAssignment,
      });
      setGateEdit(
        ruled.dockAssignment !== effectiveDock()
          ? { status, dockAssignment: ruled.dockAssignment }
          : { status },
      );
    }
    const stamp = new Date().toISOString();
    if (
      (status === ARRIVED || status === CALLED) &&
      !formData[arrivalField]
    ) {
      setArrival(stamp, formData);
    } else if (status === DOCKED) {
      handleGateAtDock(stamp);
    } else if (status === DEPARTED && canStampRelease(formData)) {
      setRelease(stamp);
    } else if (
      status === CANCELLED &&
      !formData[placedField] &&
      formData[arrivalField]
    ) {
      setArrival(null, formData);
    }
  };

  return {
    setGateEdit,
    effectiveGateStatus,
    setGateStatusForward,
    startManualGateCheckIn,
    handleGateStatus,
    handleGateDock,
    handleGateAtDock,
  };
};
